/*global chrome*/

import React from 'react';
import { observer } from 'mobx-react';
import { toJS } from 'mobx';

// Config
import { config } from '../../config';

// Helpers
import { getParam, testGetUserMedia, versionCompare } from '../../helpers/helpers';

// Translations
import translate from '../Translate.js';

// Dependencies
import browserUpdate from 'browser-update';
import { FeatureFlag } from 'react-launch-darkly';

// Components
import Step from '../Common/Step';
import Example from './Example';
import Intro from './Intro';
import DesktopApp from './DesktopApp';
import Consent from './Consent';
import SystemCheck from './SystemCheck';
import ShareScreen from './ShareScreen';
import IDVerification from './IDVerification';
import RoomScan from './RoomScan';
import ProctorPause from './ProctorPause';
import Begin from './Begin';

const SessionStartAutomated = observer(
  class SessionStartAutomated extends React.Component {
    constructor() {
      super();

      // Bind this to functions
      this.checkForExtension = this.checkForExtension.bind(this);
      this.receiveMessage = this.receiveMessage.bind(this);
      this.nextStep = this.nextStep.bind(this);
      this.previousStep = this.previousStep.bind(this);
      this.setDevice = this.setDevice.bind(this);
      this.checkForFaces = this.checkForFaces.bind(this);
      this.sendMessage = this.sendMessage.bind(this);
      this.renderManualInstall = this.renderManualInstall.bind(this);
      this.renderUnsupported = this.renderUnsupported.bind(this);
      this.restartExtension = this.restartExtension.bind(this);
      this.checkScreenShare = this.checkScreenShare.bind(this);
      this.setupSteps = this.setupSteps.bind(this);
      this.screenSharePopup = this.screenSharePopup.bind(this);
      this.proctorApproved = this.proctorApproved.bind(this);
      this.getSessionSyncDoc = this.getSessionSyncDoc.bind(this);

      this.screenError = 'User canceled screen sharing dialog.';
      this.screenPermissionError = 'Permission denied by system';
      this.permissionsError = 'Device permissions not granted.';

      // Set the initial state
      this.state = {
        late: false,
        step: 1,
        next: null,
        webcam: {},
        microphone: {},
        displayCount: null,
        unsupported: false,
        unsupportedDetals: null,
        unsupportedLink: null,
        syncDocumentData: null,
        checkingPermissions: false,
        sessionInProgress: null,
        orderedSteps: null,
        skipProctorPause: false,
        proctorApproved: false
      };
    }
    //componentDidUpdate(prevProps, prevState, snapshot) {}
    async componentDidMount() {
      const _this = this;
      let { examSid, sessionSid } = this.props;
      const { hybrid, translation, uninstall } = this.props;
      const { ExamStore, SessionStore } = this.props.store;

      let unsupported = false;

      await this.getSessionSyncDoc();

      // Adds examSid and sessionSid to session storage for exam provisioning
      sessionStorage.setItem('examSid', examSid);
      sessionStorage.setItem('sessionSid', sessionSid);

      // Make sure drawer open class isn’t present
      if (document.body.className.indexOf('drawer-open') > -1) {
        document.body.classList.remove('drawer-open');
      }

      // If we don’t have the exam session data or we are
      // in a drawer (in which case we’re probably on the dashboard)
      // get the session data.
      if (!ExamStore.examSession.status || this.props.drawer) {
        ExamStore.examSession = {};

        if (!examSid || !sessionSid) {
          examSid = getParam('examSid', window.location.href);
          sessionSid = getParam('sessionSid', window.location.href);
        }

        ExamStore.getExamSession(examSid, sessionSid);
      }

      // Check that user has a supported browser
      // HACK: passing huge version numbers for
      // any browser other than Chrome, because
      // we only support Chrome
      browserUpdate({
        required: {
          c: 74,
          f: 1000,
          e: 1000,
          i: 1000,
          o: 1000,
          o_a: 1000,
          s: 1000,
          y: 1000,
          v: 1000,
          uc: 1000,
          ios: 1000,
          samsung: 1000
        },
        reminder: 0,
        reminderClosed: 0,
        noclose: true,
        no_permanent_hide: true,
        onshow: function () {
          _this.setState({
            unsupported: true,
            unsupportedDetails: translation.unsupported_msg.browser
          });

          unsupported = true;
        }
      });

      if (
        navigator.userAgent.indexOf('Edg/') > -1 ||
        navigator.userAgent.indexOf('Edge/') > -1
      ) {
        _this.setState({
          unsupported: true,
          unsupportedDetails: translation.unsupported_msg.browser
        });

        unsupported = true;
      }

      // Get the exam start data
      ExamStore.getSessionStart(examSid, sessionSid, hybrid, sessionStart => {
        const { session } = sessionStart;

        console.log('sessionStart session data:', session);

        if (
          session &&
          session.status &&
          session.status.toLowerCase() !== 'scheduled' &&
          session.status.toLowerCase() !== 'checked in' // DMV uses this status
        ) {
          unsupported = true;

          this.setState({
            unsupported: true,
            unsupportedDetails: translation.unsupported_msg.session_closed
          });
        }

        // Make sure the chrome variable exists
        // otherwise, this user is on mobile
        if (
          typeof chrome === 'undefined' ||
          /Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        ) {
          unsupported = true;

          this.setState({
            unsupported: true,
            unsupportedDetails: translation.unsupported_msg.browser
          });
        }

        if (!unsupported && chrome.runtime) {
          this.getDisplayCount(SessionStore.extensionInfo.extensionId);

          if (!uninstall) {
            this.checkForExtension(sessionStart);
          } else {
            // NOTE: This delay is in here to make sure the extension
            // has had time to be uninstalled if we are forcing reinstallation.
            // It may not be necessary, but if checkForExtension() happens
            // to be called before the extension is actually gone, the user
            // would be able to proceed.
            unsupported = true;

            this.setState({
              unsupported,
              unsupportedDetails: translation.unsupported_msg.extension,
              unsupportedLink: config.extensionUrl,
              checkingPermissions: false
            });

            setTimeout(() => {
              this.checkForExtension(sessionStart);
            }, 1000);
          }

          this.checkForExtension(sessionStart);
        } else if (!chrome.runtime) {
          this.checkForExtension(sessionStart);
        }

        this.setupSteps();

        this.checkScreenShare();
      });

      if (unsupported) {
        return;
      }

      // Make sure getUserMedia is supported
      const getUserMediaSupported = testGetUserMedia(details => {
        _this.setState({
          unsupported: true,
          unsupportedDetails: details,
          unsupportedLink: details.linkUrl,
          checkingPermissions: false
        });
      });

      if (!getUserMediaSupported) {
        this.setState({
          next: null
        });
      }

      if(this.state.step === 1 && chrome.runtime){
        const modifiedDate = await SessionStore.returnCurrentTime();
        chrome.runtime.sendMessage(
          SessionStore.extensionInfo.extensionId,
          { message: 'onboardingStatusChange' , data: {modifiedDate: modifiedDate, statusName: "Using Online Proctoring"}},
        );
      }
      // Listen for messages from the extension
      window.addEventListener('message', this.receiveMessage, false);
    }

    async componentDidUpdate(prevProps, prevState, snapshot) {
      const { SessionStore } = this.props.store;

      if(prevState.step !== this.state.step){

        const modifiedDate =  await SessionStore.returnCurrentTime();
        const statusName = this.state.orderedSteps[this.state.step - 1];
        const previousStatus = this.state.orderedSteps[this.state.step - 2];

        chrome.runtime.sendMessage(
          SessionStore.extensionInfo.extensionId,
          { message: 'onboardingStatusChange' , data: {modifiedDate: modifiedDate, statusName: statusName, previousStatus: previousStatus}},
        );
      }

    }

    componentWillUnmount() {
      const { ExamStore } = this.props.store;

      ExamStore.sessionStartData = null;
      this.setState({ sessionInProgress: null });
    }

    setupSteps() {
      const { store, translation } = this.props;
      const { ExamStore } = store;
      const { sessionStartData } = ExamStore;
      const { modalityOptions, exam } = sessionStartData;
      const { configuration } = exam;
      const steps = toJS(translation.steps);

      let workflowModule = null;

      if (ExamStore.examSession && ExamStore.examSession.location) {
        workflowModule = ExamStore.examSession.location.workflowModule;
      }

      if (!configuration.modalityOptions) {
        configuration.modalityOptions = sessionStartData.modalityOptions;
      }
      // Remove unnecessary steps
      if (!modalityOptions.recordScreen) {
        const shareIndex = steps.indexOf('Share Screen');
        steps.splice(shareIndex, 1);
      }

      if (!modalityOptions.verifyId) {
        const idIndex = steps.indexOf('ID Verification');
        steps.splice(idIndex, 1);
      }

      if (!modalityOptions.roomScan) {
        const scanIndex = steps.indexOf('Room Scan');
        steps.splice(scanIndex, 1);
      }

      // If this is an automated session we still want to use the proctor pause component
      // but we aren't waiting for a proctor. This renames the step in the stepper to
      // something more fitting
      if (workflowModule === 'automated') {
        const pauseIndex = steps.indexOf('Proctor Pause')
        steps.splice(pauseIndex, 1, 'Waiting')
      }

      if (!modalityOptions.desktopAppCheck) {
        const scanIndex = steps.indexOf('SecureSession Utility App Check');
        steps.splice(scanIndex, 1);
      }

      // Store the steps in state
      this.setState({ orderedSteps: steps });
    }

    checkForExtension(sessionStart) {
      const _this = this;
      const { translation, sessionSid, examSid } = this.props;
      const { ExamStore, SessionStore } = this.props.store;

      // If no versions of our extension are installed,
      // chrome.runtime can end up being undefined. In
      // that situation, onboarding completely breaks.
      // So check if chrome.runtime exists and if it
      // doesn’t, flag it as unsupported and start listening
      // for the extension to be installed.
      if (!chrome.runtime) {
        console.log('Chrome runtime not defined... no extension is installed');

        if(!_this.installLoop){
          const annotationType = {
            title: "Whoops!",
            severity: "green",
            sessionTime: 0,
          }
          ExamStore.raiseAnnotation(examSid, sessionSid, annotationType)
        }



        _this.setState({
          unsupported: true,
          unsupportedDetails: translation.unsupported_msg.extension,
          unsupportedLink: config.extensionUrl,
          checkingPermissions: false
        });

        if (!_this.installLoop) {
          _this.installLoop = setInterval(() => {
            _this.checkForExtension(sessionStart);
          }, 1000);
          if(chrome.runtime){
            SessionStore.getCurrentTime();
            const modifiedDate = SessionStore.currentTime;
            chrome.runtime.sendMessage(
              SessionStore.extensionInfo.extensionId,
              { message: 'onboardingStatusChange' , data: {modifiedDate: modifiedDate, statusName: "Using Online Proctoring"}},
            );
          }
        }

        return;
      }

      chrome.runtime.sendMessage(
        SessionStore.extensionInfo.extensionId,
        { message: 'getVersionNumber' },
        response => {
          if (
            !response ||
            versionCompare(response, SessionStore.extensionInfo.version) < 0
          ) {
            if (!response) {
              console.log('Extension not installed');
              if (
                _this.state.unsupportedDetails !== translation.unsupported_msg.extension
              ) {
                _this.setState({
                  unsupported: true,
                  unsupportedDetails: translation.unsupported_msg.extension,
                  unsupportedLink: config.extensionUrl,
                  checkingPermissions: false
                });
              }
            } else {
              console.log('Old extension version installed');
              if (
                _this.state.unsupportedDetails !==
                translation.unsupported_msg.extension_version
              ) {
                _this.setState({
                  unsupported: true,
                  unsupportedDetails: translation.unsupported_msg.extension_version,
                  unsupportedLink: config.extensionUrl,
                  checkingPermissions: false,
                  unSupportedLinkCallback: e => {
                    const { href } = e.target;
                    e.preventDefault();
                    chrome.runtime.sendMessage(
                      SessionStore.extensionInfo.extensionId,
                      { message: 'uninstall' },
                      response => {
                        if (response && response.status) {
                          console.log('Did not uninstall');
                        } else {
                          window.open(href);
                        }
                      }
                    );
                  }
                });
              }
            }
            if (!_this.installLoop) {
              _this.installLoop = setInterval(() => {
                _this.checkForExtension(sessionStart);
              }, 1000);
            }
            return;
          } else {
            this.setState({
              unsupported: false,
              unsupportedDetails: null,
              unsupportedLink: null
            });
            chrome.runtime.sendMessage(
              SessionStore.extensionInfo.extensionId,
              { message: 'checkCurrentSession' },
              response => {
                // checkForExtension gets ran twice, because of this
                // I added a null check (which is sessionInProgress's default value)
                // so that if sessionInProgress is already defined from the first run
                // it stays the same
                if (this.state.sessionInProgress === null) {
                  this.setState({ sessionInProgress: response });
                }
              }
            );
            _this.getDisplayCount(SessionStore.extensionInfo.extensionId);
            clearInterval(_this.installLoop);
            // Check if permissions need to be reset
            this.sendMessage(
              {
                message: 'permissionsCheckPreOnboarding'
              },
              response => {
                console.log(
                  `Checked extension permissions. ${
                    response === false ? 'Update' : 'No update'
                  } required.`
                );
                this.setState({ checkingPermissions: true });
                // If the response from this message is false,
                // it means the extension needs to be reloaded.
                // We should also reload the UI to make sure
                // session start gets sent properly.
                if (response === false && !this.restartDelay) {
                  this.restartDelay = setInterval(() => {
                    this.sendMessage(
                      {
                        message: 'readyForRestart'
                      },
                      ready => {
                        if (ready) {
                          clearInterval(this.restartDelay);
                          location.reload();
                        }
                      }
                    );
                  }, 500);
                  return;
                }
                // If the response from this message is true,
                // the extension is good to go, so we need to
                // send session start message to extension.
                this.sendMessage({
                  message: 'startSession',
                  sessionStart,
                  token: SessionStore.token
                });
                this.setState({ checkingPermissions: false });
              }
            );
            if (ExamStore.sessionStartData) {
              ExamStore.sessionStartData['sessionEndRedirectUrl'] =
                config.sessionEndRedirectUrl;
              _this.sendMessage({
                message: 'startSession',
                sessionStart: ExamStore.sessionStartData,
                token: SessionStore.token
              });
            }
            return;
          }
        }
      );
    }

    proctorApproved(decision) {
      this.setState({ proctorApproved: decision });
    }

    async getSessionSyncDoc() {
      const { MessageStore } = this.props.store;
      return this.sendMessage({message: 'getSessionSyncDoc'}, response => {
        if(response.document) {
          if (response.document.faceProcessorStarted) {
            MessageStore.ghostRunning = true;
          }
        }
      })
    }

    getDisplayCount(extensionId) {
      const _this = this;
      const { ExamStore } = this.props.store;
      const { sessionStartData } = ExamStore;
      let modalityOptions = null;

      if (sessionStartData) {
        modalityOptions = sessionStartData.exam.configuration.modalityOptions;
      }

      chrome.runtime.sendMessage(
        extensionId,
        { message: 'getDisplayCount' },
        response => {
          this.setState({
            displayCount: response.displayCount
          });
          // For calls not for SystemCheck
          if (
            response.displayCount > 1 &&
            modalityOptions &&
            modalityOptions.onlyOneScreen
          ) {
            setTimeout(function () {
              _this.getDisplayCount(extensionId);
            }, 2000);
          }
        }
      );
    }

    setDisplays(numberOfDisplays) {
      this.setState({
        displayCount: numberOfDisplays
      });
    }

    receiveMessage(e) {
      const { translation } = this.props;
      const { ExamStore, SessionStore } = this.props.store;
      const { sessionStartData } = ExamStore;
      let modalityOptions = null;
      if (e && e.data && e.data.message && e.data.message === 'syncDocUpdated') {
        this.setState({ syncDocumentData: e.data.data });
      }

      if (sessionStartData) {
        modalityOptions = sessionStartData.exam.configuration.modalityOptions;
      }

      // Check if e.data even exists before checking for extensionId
      if (
        !e.data ||
        !SessionStore.extensionInfo ||
        e.data.extensionId !== SessionStore.extensionInfo.extensionId
      ) {
        return;
      }

      // XXX Not sure if this needed
      if (e.errorMessage && e.errorMessage.includes(this.permissionsError)) {
        return;
      }

      // Update display count if message includes count
      if (e.data.displayCount) {
        this.setState({
          displayCount: e.data.displayCount
        });

        if (
          e.data.displayCount > 1 &&
          modalityOptions &&
          modalityOptions.onlyOneScreen &&
          this.state.step > 2
        ) {
          this.setState({
            unsupported: true,
            unsupportedDetails: translation.unsupported_msg.displays,
            checkingPermissions: false
          });
        } else {
          this.setState({
            unsupported: false,
            unsupportedDetails: null
          });
        }
      }
    }

    statusClass(step) {
      if (this.state.step === step) {
        return 'slide-active';
      } else if (this.state.step > step) {
        return 'slide-hidden-before';
      } else {
        return 'slide-hidden-after';
      }
    }

    nextStep(e) {
      const steps = this.state.orderedSteps;
      const { SessionStore } = this.props.store;

      chrome.runtime.sendMessage(
        SessionStore.extensionInfo.extensionId,
        { message: 'checkCurrentSession' },
        response => {
            this.setState({ sessionInProgress: response });
        }
      );

      if (e) {
        e.preventDefault();
      }

      // Increment the step
      if (this.state.step !== steps.length) {
        this.setState({
          step: this.state.step + 1
        });
      } else {
        this.handleSubmit();
      }


    }

    async checkForFaces(callback) {
      const devices = {
        audio: this.state.microphone,
        video: this.state.webcam
      };

      this.sendMessage({ message: 'detectFaceOnWebcam', devices }, callback);
    }

    previousStep(e) {

      if (e) {
        e.preventDefault();
      }

      // Decrement the step
      this.setState({
        step: this.state.step - 1
      });
    }

    setDevice(type, device) {
      // Device is set as a comma-separated string bc setting
      // it to an object results in [object, object]
      const deviceProps = device.split(',');
      const deviceData = {
        deviceId: deviceProps[0],
        name: deviceProps[1]
      };

      this.setState({
        [type]: deviceData
      });
    }

    restartExtension() {
      const { SessionStore } = this.props.store;
      chrome.runtime.sendMessage(
        SessionStore.extensionInfo.extensionId,
        { message: 'restartExtension' },
        response => {
          if (response === 'reloading') {
            location.reload();
          }
        }
      );
    }

    sendMessage(data, callback) {
      const { translation } = this.props;
      const { SessionStore, ExamStore } = this.props.store;

      if(!chrome.runtime){
        this.checkForExtension(ExamStore.sessionStartData);
        return
      }
      chrome.runtime.sendMessage(
        SessionStore.extensionInfo.extensionId,
        data,
        response => {
          if (callback) {
            callback(typeof response !== 'undefined' ? response : 'no response');
            return;
          }

          if (!response.status === 'ERROR' || !response.errorMessage) {
            return;
          }

          if (response.errorMessage.includes(this.permissionsError)) {
            // User did not grant access to their camera/mic/etc
            return;
          }

          if (response.errorMessage.includes(this.screenError)) {
            // TODO: If user declines, listener stops and will not accept
            // User declined screen recording, try again
            this.sendMessage(data);
          } else if (
            response.errorMessage.includes(this.screenPermissionError) &&
            navigator.userAgent.indexOf('Mac') > -1 &&
            navigator.userAgent.indexOf('10_15') > -1
          ) {
            // User is on Catalina and screen sharing failed
            // Show an message directing them to help doc
            SessionStore.showDialog(translation.catalina_error);
          } else {
            SessionStore.throwError(response.errorMessage);
            return;
          }
        }
      );
    }

    checkScreenShare() {
      const { SessionStore, ExamStore } = this.props.store;
      if(!chrome.runtime){
        this.checkForExtension(ExamStore.sessionStartData);
        return
      }

      let shareIndex = this.state.orderedSteps.indexOf('Share Screen');

      chrome.runtime.sendMessage(
        SessionStore.extensionInfo.extensionId,
        { message: 'isRecording' },
        response => {
          if (response.isScreenRecording === true) {
            chrome.runtime.sendMessage(
              SessionStore.extensionInfo.extensionId,
              { message: 'getSessionStart' },
              response => {
                if (response.session.sid === sessionStorage.getItem('sessionSid')) {
                  this.setState({ step: shareIndex + 2 });
                }
              }
            );
          }
        }
      );
    }

    handleSubmit() {
      const { ExamStore } = this.props.store;
      const { sessionStartData } = ExamStore;

      this.sendMessage({
        message: 'onboardingFinished'
      });


      window.top.location = sessionStartData.exam.url;
    }

    screenSharePopup() {
      const devices = {
        audio: this.state.microphone,
        video: this.state.webcam
      };

      this.sendMessage({ message: 'startSessionRecording', devices });
    }

    renderManualInstall() {
      const { translation } = this.props;

      return (
        <div>
          <p>{translation.unsupported_msg.extension_manual.body}</p>

          {this.state.unsupportedDetails.video && (
            <div className="vid__wrap vid__wrap--wide">
              <iframe
                className="vid__video"
                src={this.state.unsupportedDetails.video}
                width="640"
                height="488"
                frameBorder="0"
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            </div>
          )}

          {this.state.unsupportedDetails.link && (
            <a
              className="btn"
              href="https://smarterproctoring.zendesk.com/hc/en-us/articles/360040636812"
              target="_blank"
            >
              {translation.unsupported_msg.extension_manual.link}
            </a>
          )}
        </div>
      );
    }

    renderUnsupported() {
      return (
        <div>
          <p>{this.state.unsupportedDetails.body}</p>

          {this.state.unsupportedDetails.video && (
            <div className="vid__wrap vid__wrap--wide">
              <iframe
                className="vid__video"
                src={this.state.unsupportedDetails.video}
                width="640"
                height="488"
                frameBorder="0"
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            </div>
          )}

          {this.state.unsupportedDetails.link && (
            <a
              className="btn"
              href={
                this.state.unsupportedDetails.url
                  ? this.state.unsupportedDetails.url
                  : this.state.unsupportedLink
              }
              target="_blank"
              onClick={e =>
                this.state.unSupportedLinkCallback
                  ? this.state.unSupportedLinkCallback(e)
                  : null
              }
            >
              {this.state.unsupportedDetails.link}
            </a>
          )}
        </div>
      );
    }

    /**
     * Render the system check view and use LaunchDarkly flag
     * to determine whether or not to do a face check.
     *
     * @function renderSystemCheck
     * @param {boolean} detectFace - LaunchDarkly flag status
     */
    renderSystemCheck = detectFace => {
      const { sessionStartData } = this.props.store.ExamStore;
      const examConfiguration = sessionStartData.exam.configuration;

      return (
        <SystemCheck
          {...this.props}
          parent={this}
          statusClass={this.statusClass(3)}
          nextStep={this.nextStep}
          button={this.submit}
          examConfiguration={examConfiguration}
          webcam={this.state.webcam.deviceId}
          microphone={this.state.microphone.deviceId}
          displayCount={this.state.displayCount}
          setDevice={this.setDevice}
          runFaceCheck={detectFace ? true : false}
          faceCheck={this.checkForFaces}
        />
      );
    };

    render() {
      const { external, translation } = this.props;
      const { ExamStore, SessionStore } = this.props.store;
      const { orderedSteps } = this.state;
      const { sessionStartData } = ExamStore;
      let workflowModule;
      let proctorPausePage;

      if (orderedSteps){
        if(orderedSteps.indexOf('Proctor Pause') === -1) {
          proctorPausePage = orderedSteps.indexOf('Waiting')
        } else {
          proctorPausePage = orderedSteps.indexOf('Proctor Pause')
        }
      }


      if (!sessionStartData || !translation || !this.state.orderedSteps) {
        return null;
      }
      if(ExamStore.examSession && ExamStore.examSession.location) {
        workflowModule = ExamStore.examSession.location.workflowModule
      }

      const examSession = sessionStartData.session;
      const examConfiguration = sessionStartData.exam.configuration;
      const { agreements } = sessionStartData;

      // The session start data doesn’t include workflow module
      // so we need to get it from the exam session data that’s
      // stored in the ExamStore.

      if (!examConfiguration.modalityOptions) {
        examConfiguration.modalityOptions = sessionStartData.modalityOptions;
      }

      // Remove unnecessary steps

      if (this.state.checkingPermissions) {
        return (
          <div className="panel panel--medium">
            <span className="no-results">{translation.permissions_check}</span>
          </div>
        );
      }

      if (this.state.unsupported) {
        return (
          <div
            className={`drawer__content${external ? ' drawer__content--external' : ''}`}
          >
            <header className="drawer__header">
              <div className="wrap">
                <h1 className="drawer__title">Start Session</h1>
                {external}
              </div>
            </header>

            <div className="page--medium">
              <div className="panel panel--medium">
                <h2 className="panel__title panel__title--alt">
                  {translation.unsupported_heading}
                </h2>

                {/* If the extension is not installed, check if it needs to be installed manually */}
                {this.state.unsupportedDetails.body ===
                  translation.unsupported_msg.extension.body && (
                  <FeatureFlag
                    flagKey="extension-manual-install"
                    renderFeatureCallback={this.renderManualInstall}
                    renderDefaultCallback={this.renderUnsupported}
                  />
                )}

                {/* Otherwise, just show the unsupported message */}
                {this.state.unsupportedDetails.body !==
                  translation.unsupported_msg.extension.body && this.renderUnsupported()}
              </div>
            </div>
          </div>
        );
      } else if (this.state.sessionInProgress) {
        return (
          <div
            className={`drawer__content${external ? ' drawer__content--external' : ''}`}
          >
            <header className="drawer__header">
              <div className="wrap">
                <h1 className="drawer__title">Start Session</h1>
                {external}
              </div>
            </header>

            <div className="page--medium">
              <div className="panel panel--medium">
                <h2 className="panel__title panel__title--alt">
                  {translation.sessionInProgress.heading}
                </h2>
                <p>{translation.sessionInProgress.body}</p>
                <p className="note">{translation.sessionInProgress.warning}</p>
                <button onClick={this.restartExtension} className="btn btn--negative">
                  {translation.sessionInProgress.button}
                </button>
              </div>
            </div>
          </div>
        );
      } else {
        return (
          <div
            className={`drawer__content${external ? ' drawer__content--external' : ''}`}
          >
            <header className="drawer__header">
              <div className="wrap">
                <h1 className="drawer__title">Start Session</h1>
              </div>
            </header>

            <div className="page--medium">
              <Step
                step={this.state.step}
                setStep={this.setStep}
                data={this.state.orderedSteps}
                skippable={false}
              />

              <div className="slide-wrap wrap">
                <Example
                  {...this.props}
                  parent={this}
                  statusClass={this.statusClass(orderedSteps.indexOf('Welcome') + 1)}
                  nextStep={this.nextStep}
                  button={this.submit}
                />

                <Intro
                  {...this.props}
                  parent={this}
                  statusClass={this.statusClass(orderedSteps.indexOf('Exam Details') + 1)}
                  examConfiguration={examConfiguration}
                  nextStep={this.nextStep}
                  button={this.submit}
                />

                <FeatureFlag
                  flagKey="automated-face-detection"
                  renderFeatureCallback={this.renderSystemCheck}
                  renderDefaultCallback={this.renderSystemCheck}
                />

                <DesktopApp
                  {...this.props}
                  parent={this}
                  statusClass={this.statusClass(
                    orderedSteps.indexOf('SecureSession Utility App Check') + 1
                  )}
                  nextStep={this.nextStep}
                  button={this.submit}
                  syncDocumentData={this.state.syncDocumentData}
                  sessionStartData={sessionStartData}
                />

                <Consent
                  {...this.props}
                  agreements={agreements}
                  parent={this}
                  statusClass={this.statusClass(orderedSteps.indexOf('Consent') + 1)}
                  examSession={examSession}
                  nextStep={this.nextStep}
                  button={this.submit}
                  onComplete={devices => {
                    this.sendMessage({ message: 'event_systemCheck' });

                    // Start recording if it has not started yet
                    // chrome.runtime.sendMessage(
                    //   SessionStore.extensionInfo.extensionId,
                    //   { message: 'isRecording' },
                    //   response => {
                    //     if (!response.isScreenRecording) {
                    //       this.sendMessage({ message: 'startSessionRecording', devices });
                    //     }
                    //   }
                    // );
                  }}
                />

                <ShareScreen
                  {...this.props}
                  parent={this}
                  statusClass={this.statusClass(orderedSteps.indexOf('Share Screen') + 1)}
                  nextStep={this.nextStep}
                  button={this.submit}
                  sessionStartData={sessionStartData}
                  onShowPopup={this.screenSharePopup}
                  step={this.state.step}
                  proctorApproved={this.state.proctorApproved}
                />

                <IDVerification
                  {...this.props}
                  parent={this}
                  statusClass={this.statusClass(
                    orderedSteps.indexOf('ID Verification') + 1
                  )}
                  nextStep={this.nextStep}
                  button={this.submit}
                  webcam={this.state.webcam.deviceId}
                  sessionStartData={sessionStartData}
                  smarterId={
                    sessionStartData.smarterId &&
                    sessionStartData.smarterId.userSid &&
                    SessionStore.settings.smarterIdEnabled
                      ? true
                      : false
                  }
                  onCapture={type => {
                    this.sendMessage({ message: 'event_photoTaken', type });
                  }}
                  onAccept={(type, photo) => {
                    this.sendMessage({ message: 'event_photoAccepted', type });

                    if (photo)
                      this.sendMessage({ message: 'receivePhoto', base64: photo });
                  }}
                />

                <RoomScan
                  {...this.props}
                  parent={this}
                  statusClass={this.statusClass(orderedSteps.indexOf('Room Scan') + 1)}
                  nextStep={this.nextStep}
                  button={this.submit}
                  sessionStartData={sessionStartData}
                  webcam={this.state.webcam.deviceId}
                  onStart={() => this.sendMessage({ message: 'event_roomScanStart' })}
                  onStop={() => this.sendMessage({ message: 'event_roomScanStop' })}
                />

                <ProctorPause
                  {...this.props}
                  parent={this}
                  statusClass={this.statusClass(
                    proctorPausePage + 1
                  )}
                  automated={workflowModule === 'automated'}
                  nextStep={this.nextStep}
                  button={this.submit}
                  skipProctorPause={this.state.skipProctorPause}
                  proctorApproved={this.state.proctorApproved}
                  setProctorApproved={this.proctorApproved}
                />

                <Begin
                  {...this.props}
                  parent={this}
                  statusClass={this.statusClass(orderedSteps.indexOf('Begin') + 1)}
                  nextStep={this.nextStep}
                  button={this.submit}
                />
              </div>
            </div>

            <div className="drawer__action">
              {this.state.step > 1 && this.state.step < 5 && (
                <button
                  className="btn btn--outline drawer__back"
                  onClick={this.previousStep}
                >
                  Back
                </button>
              )}

              <button
                className="btn"
                onClick={this.state.next}
                ref={button => (this.submit = button)}
              >
                Continue
              </button>
            </div>
          </div>
        );
      }
    }
  }
);

export default translate('SessionStartAutomated')(SessionStartAutomated);

/**
// Example of getting display count from extension
var editorExtensionId = "mehgceidjddddencjmonjkbcponoakag";

// Make a simple request:
chrome.runtime.sendMessage(editorExtensionId, {message: "getDisplayCount"},
  function(response) {
    console.log(response.displayCount)
  }
);
*/



// WEBPACK FOOTER //
// ./src/app/components/SessionStart/SessionStartAutomated.js